import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import { AlertTriangle, PackageX, PackageOpen, RefreshCw, Clock, Globe } from 'lucide-react';

// ⭐ Pantalla "Alertas": centraliza en un solo lugar dos cosas que ya
// existían pero estaban enterradas dentro de Inventario (antes Productos):
// stock bajo y lotes por vencer. No se agregan endpoints nuevos — reusa
// GET /api/pos/products (ya existente) y GET /api/pos/batches/expiring
// (ya existente, de la fase de lotes/PEPS). Deliberadamente NO se toca
// LotesManager.js ni su <ProductosPorVencerPanel/> (siguen funcionando
// igual dentro de Inventario) — esta pantalla es una vista adicional, no
// un reemplazo.

const LOW_STOCK_THRESHOLD = 5; // mismo umbral visual que ya usa ProductosManager.js

const DAY_WINDOWS = [7, 15, 30, 60];

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value + 'T12:00:00');
  if (isNaN(d.getTime())) return value;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function daysUntil(value) {
  if (!value) return null;
  const target = new Date(value + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
}

function urgencyStyle(days) {
  if (days < 0) return 'bg-red-100 text-red-700';
  if (days <= 7) return 'bg-orange-100 text-orange-700';
  if (days <= 15) return 'bg-yellow-100 text-yellow-700';
  return 'bg-blue-100 text-blue-700';
}

function urgencyLabel(days) {
  if (days < 0) return `Vencido hace ${Math.abs(days)}d`;
  if (days === 0) return 'Vence hoy';
  return `${days}d restantes`;
}

// ⭐ Countdown de pruebas gratuitas (POS 15 días, Menú Web 3 días): mientras
// falte más de un día se muestra en días (3, 2, 1); dentro del último día se
// muestra en horas (24h, 23h, ...1h) para transmitir más urgencia día a día.
// `dateOnly` es true para payment_due (columna DATE, sin hora) -- en ese
// caso se interpreta como "vence al final de ese día" para poder seguir
// contando en horas durante las últimas 24h.
function parseTrialEnd(value, dateOnly) {
  if (!value) return null;
  const d = dateOnly
    ? new Date(`${value}T23:59:59Z`)
    : new Date(String(value).replace(' ', 'T') + 'Z');
  return isNaN(d.getTime()) ? null : d;
}

function formatTrialCountdown(value, dateOnly) {
  const end = parseTrialEnd(value, dateOnly);
  if (!end) return null;
  const msLeft = end.getTime() - Date.now();
  if (msLeft <= 0) return { label: 'Prueba vencida', urgent: true, expired: true };
  const totalHours = Math.ceil(msLeft / 3600000);
  if (totalHours > 24) {
    const days = Math.ceil(totalHours / 24);
    return { label: `${days} día${days === 1 ? '' : 's'} restantes`, urgent: days <= 1, expired: false };
  }
  return { label: `${totalHours}h restantes`, urgent: true, expired: false };
}

function TrialCountdownCard({ icon: Icon, title, description, value, dateOnly }) {
  const countdown = formatTrialCountdown(value, dateOnly);
  if (!countdown) return null;
  const tone = countdown.expired
    ? 'bg-red-50 border-red-300 text-red-700'
    : countdown.urgent
      ? 'bg-orange-50 border-orange-300 text-orange-700'
      : 'bg-blue-50 border-blue-300 text-blue-700';
  return (
    <div className={`rounded-lg border p-4 flex items-start gap-3 ${tone}`}>
      <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="font-bold text-sm">{title}</p>
        <p className="text-xs opacity-80">{description}</p>
      </div>
      <span className="text-sm font-bold whitespace-nowrap">{countdown.label}</span>
    </div>
  );
}

export default function AlertasManager({ token }) {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [expiringDays, setExpiringDays] = useState(30);
  const [expiringItems, setExpiringItems] = useState([]);
  const [loadingExpiring, setLoadingExpiring] = useState(true);
  const [trialStatus, setTrialStatus] = useState(null);

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const res = await axios.get(`${API_URL}/api/pos/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(res.data || []);
    } catch (err) {
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [token]);

  const loadExpiring = useCallback(async () => {
    setLoadingExpiring(true);
    try {
      const res = await axios.get(`${API_URL}/api/pos/batches/expiring`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { days: expiringDays }
      });
      setExpiringItems(res.data || []);
    } catch (err) {
      setExpiringItems([]);
    } finally {
      setLoadingExpiring(false);
    }
  }, [token, expiringDays]);

  const loadTrialStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/pos/account-trial-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrialStatus(res.data);
    } catch (err) {
      setTrialStatus(null);
    }
  }, [token]);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { loadExpiring(); }, [loadExpiring]);
  useEffect(() => { loadTrialStatus(); }, [loadTrialStatus]);

  const lowStockProducts = products
    .filter(p => p.use_stock && Number(p.stock) <= LOW_STOCK_THRESHOLD)
    .sort((a, b) => Number(a.stock) - Number(b.stock));

  const outOfStock = lowStockProducts.filter(p => Number(p.stock) <= 0);
  const vencidos = expiringItems.filter(it => daysUntil(it.expiration_date) < 0).length;

  const refreshAll = () => { loadProducts(); loadExpiring(); loadTrialStatus(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            Alertas
          </h2>
          <p className="text-sm text-gray-500">Stock bajo y productos por vencer, en un solo lugar.</p>
        </div>
        <button
          onClick={refreshAll}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Actualizar
        </button>
      </div>

      {/* Pruebas gratuitas: POS (15 días) y Menú Web (3 días) */}
      {trialStatus && (trialStatus.pos_trial_active || trialStatus.menu_trial_active) && (
        <div className="space-y-3">
          {trialStatus.pos_trial_active && (
            <TrialCountdownCard
              icon={Clock}
              title="Prueba gratuita del POS"
              description="Tu cuenta está en periodo de prueba. Actualiza tu plan antes de que termine para no perder el acceso."
              value={trialStatus.pos_trial_ends_at}
              dateOnly
            />
          )}
          {trialStatus.menu_trial_active && (
            <TrialCountdownCard
              icon={Globe}
              title="Prueba gratuita del Menú Web Estático"
              description="Estás probando el menú digital gratis. Actualiza a plan Premium antes de que termine para conservarlo."
              value={trialStatus.menu_trial_ends_at}
            />
          )}
        </div>
      )}

      {/* Resumen rápido */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={`rounded-lg p-4 border ${outOfStock.length > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
          <p className="text-xs text-gray-500">Sin stock</p>
          <p className={`text-2xl font-bold ${outOfStock.length > 0 ? 'text-red-600' : 'text-gray-700'}`}>{outOfStock.length}</p>
        </div>
        <div className={`rounded-lg p-4 border ${lowStockProducts.length > outOfStock.length ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200'}`}>
          <p className="text-xs text-gray-500">Stock bajo (≤{LOW_STOCK_THRESHOLD})</p>
          <p className="text-2xl font-bold text-yellow-700">{lowStockProducts.length}</p>
        </div>
        <div className={`rounded-lg p-4 border ${vencidos > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
          <p className="text-xs text-gray-500">Lotes vencidos</p>
          <p className={`text-2xl font-bold ${vencidos > 0 ? 'text-red-600' : 'text-gray-700'}`}>{vencidos}</p>
        </div>
      </div>

      {/* Stock bajo */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <PackageX className="w-5 h-5 text-red-500" />
          Productos con stock bajo
        </h3>
        {loadingProducts ? (
          <p className="text-center text-gray-500 py-6">Cargando...</p>
        ) : lowStockProducts.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <PackageOpen className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p>Ningún producto con stock bajo ahora mismo.</p>
          </div>
        ) : (
          <div className="divide-y">
            {lowStockProducts.map(p => (
              <div key={p.id} className="flex items-center justify-between py-2.5">
                <div>
                  <p className="font-semibold text-gray-800">{p.name}</p>
                  <p className="text-xs text-gray-500">{p.category}</p>
                </div>
                <span className={`text-sm font-bold px-2.5 py-1 rounded-full ${Number(p.stock) <= 0 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                  {Number(p.stock) <= 0 ? 'Sin stock' : `${p.stock} unidades`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Productos por vencer */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            Productos por vencer
          </h3>
          <div className="flex gap-2">
            {DAY_WINDOWS.map(d => (
              <button key={d} onClick={() => setExpiringDays(d)}
                className={`px-3 py-1 rounded-full text-xs font-semibold ${expiringDays === d ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {d} días
              </button>
            ))}
          </div>
        </div>
        {loadingExpiring ? (
          <p className="text-center text-gray-500 py-6">Cargando...</p>
        ) : expiringItems.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <PackageOpen className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p>Ningún lote vence dentro de {expiringDays} días.</p>
          </div>
        ) : (
          <div className="divide-y">
            {expiringItems.map((it) => {
              const d = daysUntil(it.expiration_date);
              return (
                <div key={it.id} className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="font-semibold text-gray-800">{it.product_name}</p>
                    <p className="text-xs text-gray-500">{it.quantity} unidades · vence {formatDate(it.expiration_date)}</p>
                  </div>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${urgencyStyle(d)}`}>{urgencyLabel(d)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
